import {
  BusinessAreaLookupModel,
  DivisionLookupModel,
  GuidNameCodeModel,
  HubLookupModel,
  LocalDivisionLookupModel,
  OrganisationLookupApiService,
  OrganisationLookupModel,
} from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  booleanAttribute,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormRecord,
} from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { ContainsPipe } from '../../pipes/contains.pipe';
import { InputCheck } from '../input-check/input-check.component';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { NumberBadge } from '../number-badge/number-badge.component';

type Item = BusinessAreaLookupModel | DivisionLookupModel | HubLookupModel;

type SelectedDescendants = {
  divisions: number;
  hubs: number;
  localDivisions: number;
  total: number;
};

export type OrganisationFormGroup = FormGroup<{
  divisions: FormRecord<FormControl<boolean | null>>;
  hubs: FormRecord<FormControl<boolean | null>>;
  localDivisions: FormRecord<FormControl<boolean | null>>;
}>;

@Component({
  selector: 'procure-input-organisation',
  templateUrl: './input-organisation.component.html',
  styleUrl: './input-organisation.component.scss',
  imports: [
    InputCheck,
    LoadingIndicator,
    MatIcon,
    ContainsPipe,
    MatTooltip,
    NumberBadge,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InputOrganisation {
  readonly organisations = input<OrganisationLookupModel | null>(null);

  readonly label = input<string>();
  readonly control = input.required<FormGroup>();

  readonly minimumPermissionRole = input<number>();

  readonly hasPermissions = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly onlyLocalDivision = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly external = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly showMode = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly selectAll = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly afterAllSelected = output<void>();

  protected readonly form = inject(FormBuilder).group({
    divisions: new FormRecord<FormControl<boolean | null>>({}),
    hubs: new FormRecord<FormControl<boolean | null>>({}),
    localDivisions: new FormRecord<FormControl<boolean | null>>({}),
  });

  protected readonly expanded = signal<string[]>([]);

  protected readonly organisationLookup =
    signal<OrganisationLookupModel | null>(null);

  protected readonly isLoading = signal(true);

  protected get isRequired(): boolean {
    const { validator } = this.control();

    if (!validator) {
      return false;
    }

    return validator({} as AbstractControl)?.organisation;
  }

  private skipUpdate: boolean = false;

  private readonly organisationLookupApiService = inject(
    OrganisationLookupApiService,
  );

  private readonly destroyRef = inject(DestroyRef);
  private valueSubscription?: Subscription;

  constructor() {
    this.fetchData();

    effect(() => {
      this.buildForm(this.organisationLookup());
      this.updateValueFromControl();
    });

    effect(() => {
      this.organisationLookup.set(this.organisations());
      this.isLoading.set(false);
    });

    effect(() => {
      const control = this.control();

      this.valueSubscription?.unsubscribe();

      this.valueSubscription = control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.skipUpdate) {
            return;
          }

          this.updateValueFromControl();
        });
    });

    effect(() => {
      if (this.external()) {
        return;
      }

      this.fetchOrganisationLookup(this.hasPermissions());
    });

    effect(() => {
      if (!this.selectAll()) {
        return;
      }

      this.selectAllItems();
    });
  }

  protected toggleExpansion(item: Item): void {
    if (!item.children?.length) {
      return;
    }

    this.expanded.update((state) => {
      if (state.includes(item.id)) {
        return state.filter((element) => element !== item.id);
      }

      return [...state, item.id];
    });
  }

  protected selectedDescendants(item: Item): SelectedDescendants {
    const divisions =
      item instanceof BusinessAreaLookupModel ? (item.divisions ?? []) : [];

    const hubs =
      item instanceof DivisionLookupModel
        ? (item.hubs ?? [])
        : divisions.flatMap((division) => division.hubs ?? []);

    const localDivisions =
      item instanceof HubLookupModel
        ? (item.localDivisions ?? [])
        : hubs.flatMap((hub) => hub.localDivisions ?? []);

    const selected = {
      divisions: this.onlyLocalDivision()
        ? 0
        : this.countSelected(this.form.controls.divisions, divisions),
      hubs: this.onlyLocalDivision()
        ? 0
        : this.countSelected(this.form.controls.hubs, hubs),
      localDivisions: this.countSelected(
        this.form.controls.localDivisions,
        localDivisions,
      ),
    };

    return {
      ...selected,
      total: selected.divisions + selected.hubs + selected.localDivisions,
    };
  }

  protected getSelectedTooltip(selected: SelectedDescendants): string {
    const parts = [
      this.pluralise(selected.divisions, 'division', 'divisions'),
      this.pluralise(selected.hubs, 'hub', 'hubs'),
      this.pluralise(
        selected.localDivisions,
        'local division',
        'local divisions',
      ),
    ].filter(Boolean);

    return `${parts.join(', ')} selected.`;
  }

  protected toggleItem(
    event: MatCheckboxChange,
    item: Item | LocalDivisionLookupModel,
  ): void {
    this.skipUpdate = true;

    if (item instanceof DivisionLookupModel) {
      this.toggleDivision(event, item);
    } else if (item instanceof HubLookupModel) {
      this.toggleHub(event, item);
    } else if (item instanceof LocalDivisionLookupModel) {
      this.toggleLocalDivision(event, item);
    }

    this.skipUpdate = false;
  }

  private countSelected(
    controls: FormRecord<FormControl<boolean | null>>,
    items: GuidNameCodeModel[],
  ): number {
    return items.filter((item) => !!controls.controls[item.id]?.value).length;
  }

  private pluralise(count: number, singular: string, plural: string): string {
    if (!count) {
      return '';
    }

    return `${count} ${count === 1 ? singular : plural}`;
  }

  private buildForm(lookup: OrganisationLookupModel | null): void {
    Object.keys(this.form.controls.divisions).forEach((key) => {
      (this.form.controls.divisions as FormRecord).removeControl(key);
    });

    Object.keys(this.form.controls.hubs).forEach((key) => {
      (this.form.controls.hubs as FormRecord).removeControl(key);
    });

    Object.keys(this.form.controls.localDivisions).forEach((key) => {
      (this.form.controls.localDivisions as FormRecord).removeControl(key);
    });

    if (!lookup?.businessAreas) {
      return;
    }

    lookup.businessAreas
      .flatMap((businessArea) => businessArea.divisions ?? [])
      .forEach((division) => {
        this.form.controls.divisions.addControl(
          division.id,
          new FormControl(false),
        );

        division.hubs?.forEach((hub) => {
          this.form.controls.hubs.addControl(hub.id, new FormControl(false));

          hub.localDivisions?.forEach((localDivision) => {
            this.form.controls.localDivisions.addControl(
              localDivision.id,
              new FormControl(false),
            );
          });
        });
      });
  }

  private selectAllItems(): void {
    Object.keys(this.form.controls.divisions.controls).forEach((key) => {
      this.form.controls.divisions.controls[key].setValue(true);
    });

    Object.keys(this.form.controls.hubs.controls).forEach((key) => {
      this.form.controls.hubs.controls[key].setValue(true);
    });

    Object.keys(this.form.controls.localDivisions.controls).forEach((key) => {
      this.form.controls.localDivisions.controls[key].setValue(true);
    });

    this.control().patchValue({ divisions: [], hubs: [], localDivisions: [] });

    this.organisationLookup()?.businessAreas?.forEach((businessArea) => {
      businessArea.divisions?.forEach((division) => {
        this.control().controls.divisions.setValue([
          ...this.control().value.divisions,
          division,
        ]);

        division.hubs?.forEach((hub) => {
          this.control().controls.hubs.setValue([
            ...this.control().value.hubs,
            hub,
          ]);

          hub.localDivisions?.forEach((localDivision) => {
            this.control().controls.localDivisions.setValue([
              ...this.control().value.localDivisions,
              localDivision,
            ]);
          });
        });
      });
    });

    this.afterAllSelected.emit();
  }

  private updateValueFromControl(): void {
    const control = this.control();

    if (!control) {
      return;
    }

    Object.keys(this.form.controls.divisions.controls).forEach((key) => {
      this.form.controls.divisions.controls[key].setValue(
        control.value.divisions.find(
          (item: GuidNameCodeModel) => item.id === key,
        ),
      );
    });

    Object.keys(this.form.controls.hubs.controls).forEach((key) => {
      this.form.controls.hubs.controls[key].setValue(
        control.value.hubs.find((item: GuidNameCodeModel) => item.id === key),
      );
    });

    Object.keys(this.form.controls.localDivisions.controls).forEach((key) => {
      this.form.controls.localDivisions.controls[key].setValue(
        control.value.localDivisions.find(
          (item: GuidNameCodeModel) => item.id === key,
        ),
      );
    });
  }

  private updateSelection(
    level: 'divisions' | 'hubs' | 'localDivisions',
    organisation: GuidNameCodeModel,
    checked: boolean,
  ): void {
    const others = this.control().value[level].filter(
      (item: GuidNameCodeModel) => item.id !== organisation.id,
    );

    this.control().controls[level].setValue(
      checked ? [...others, organisation] : others,
    );
  }

  private toggleLocalDivision(
    event: MatCheckboxChange,
    localDivision: LocalDivisionLookupModel,
  ): void {
    this.updateSelection('localDivisions', localDivision, event.checked);
  }

  private toggleHub(event: MatCheckboxChange, hub: HubLookupModel): void {
    hub.localDivisions?.forEach((localDivision) => {
      this.form.controls.localDivisions.controls[localDivision.id].setValue(
        event.checked,
      );
      this.toggleLocalDivision(event, localDivision);
    });

    this.updateSelection('hubs', hub, event.checked);
  }

  private toggleDivision(
    event: MatCheckboxChange,
    division: DivisionLookupModel,
  ): void {
    division.hubs?.forEach((hub) => {
      this.form.controls.hubs.controls[hub.id].setValue(event.checked);
      this.toggleHub(event, hub);
    });

    this.updateSelection('divisions', division, event.checked);
  }

  private async fetchData(): Promise<void> {
    if (this.external()) {
      return;
    }

    const minimumPermissionRole = this.minimumPermissionRole();

    let request;

    if (minimumPermissionRole !== undefined) {
      request = this.organisationLookupApiService.getOrganisationByRole(
        minimumPermissionRole,
      );
    } else {
      request = this.organisationLookupApiService.getOrganisation(
        this.hasPermissions(),
      );
    }

    const response = await request;

    this.isLoading.set(false);

    if (!response) {
      return;
    }

    this.organisationLookup.set(response);
  }

  private async fetchOrganisationLookup(
    hasPermissions: boolean,
  ): Promise<void> {
    const response =
      await this.organisationLookupApiService.getOrganisation(hasPermissions);

    this.isLoading.set(false);

    if (!response) {
      return;
    }

    this.organisationLookup.set(response);
  }
}
